import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import "./Chat.css";
import { useLocation, useNavigate } from "../../hooks/useRouter";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

// components
import NewsBanner from "../../components/NewsBanner/NewsBanner";
import MediaSection from "../../components/MediaSection/MediaSection";
import Dashboard from "../../components/Dashboard/Dashboard";
import MessageBox from "../../components/MessageBox/MessageBox";
import ChatPromptContainer from "../../components/ChatPromptContainer/ChatPromptContainer";
import PdfPreview from "../../components/Preview/Preview";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import Modal from "../../components/Modal/Modal";

// store
import {
  updateChatFeedbackRequest,
  uploadFileData,
  fetchUserConversationRequest,
  fetchDocumentCitationChunks,
  fetchFileBytesData,
  fetchChatCompletions,
  fetchEnhanceQueryRequest,
  updateWebSearchConsent,
  fetchImageToolEnhancePrompt,
  managePrompts,
  fetchProductLabelFileBytesData,
  fetchProductLabelAIChatCompletions,
  fetchChatExcelCompletions,
  handleChatExcelFileUpload
} from "../../store/apiActions";
import { setWebSearchEnabled, setFileUploaded, setSelectedGPTModal, setThinkingEnabled, setSelectedTool } from "../../store/actions";

// utility & constants
import { useEventEmitter } from "../../contexts/EventEmmiter";
import { generateCustomId, generateSessionId, scrollToLatestViewPoint, formatPrompts, formatImageData } from "../../utils/commonHelper";
import { getDocxUrlFromBase64, getFileTypeCategory, downloadFile, validateFileLimits, fileToBase64, displayInvalidFileErrors } from "../../utils/fileHelper";
import textToSpeech from "../../utils/textToSpeech";
import { validateFiles } from "../../utils/validateFiles";
import { encrypted, sessionManager } from "../../utils/utility";
import { formatMessageTime } from "../../utils/dateUtils";
import { roles } from "../../constant/roleConfig";
import { useConstants } from "../../constant/constantProvider";
import { UPL_GENIE_DOCUMENT_FILE_COUNT_LIMIT, UPL_GENIE_DOCUMENT_FILE_SIZE_LIMIT, UPL_GENIE_PROMPT_CHAR_LIMIT } from "../../constant/globalConstants";
import { sanitizeTextInput, sanitizeTextPreservingHTML } from "../../utils/textUtils";
import { handleUnauthorized } from "../../utils/apiHelper";
import { useAuth } from "../../hooks/useAuth";
import { useRbac } from "../../hooks/useRbac";
import { RBAC_APPS, UplGenieFeatures } from "../../constant/rbacConfig";
import { KEYS } from "../../constant/keyConstants";
import useIsMobile from "../../hooks/useIsMobile";
import usePrompts from "../../hooks/usePrompts";

import { getModelInfo, filterModelsByFeatures } from "../../utils/modalUtility";

// images
import botIcon from "../../assets/new-images/genie-chat-logo.svg";
import downArrow from "../../assets/new-images/dropdown-icon.svg";
import promptLibraryIcon from "../../assets/new-images/prompt-library-blue.svg";
import dailyTask from "../../assets/new-images/daily-task.svg";
import imageCreationIcon from "../../assets/new-images/image-creation-icon.svg";
import activeImageCreationIcon from "../../assets/new-images/active-image-creation-icon.svg";

// eslint-disable-next-line no-unused-vars
import dataConnectorIcon from "../../assets/new-images/data-connector-icon.svg";
// eslint-disable-next-line no-unused-vars
import productLabelIcon from "../../assets/new-images/product-label-icon.svg";
// eslint-disable-next-line no-unused-vars
import activeProductLabelIcon from "../../assets/new-images/active-product-label-icon.svg";
// eslint-disable-next-line no-unused-vars
import excelAnalysisIcon from "../../assets/new-images/poster-creation-icon.svg";
// eslint-disable-next-line no-unused-vars
import activeExcelAnalysisIcon from "../../assets/new-images/active-poster-creation-icon.svg";


function Chat() {
  const { subscribe, emit } = useEventEmitter();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    HR_ASSISTANT,
    CHAT_GENIE,
    GENIE_START_PROMPT,
    WEB_SEARCH_CONFIRM_MESSAGE,
  } = useConstants();

  const constants = useConstants();

  const staticNews = [
    {
      title: "Global Push for Sustainable Agriculture Accelerates",
      description:
        "Governments worldwide are increasing investments in eco-friendly crop protection."
    },
    {
      title: "AI in Farming is Growing Rapidly",
      description:
        "AI tools are helping farmers improve yield and reduce costs."
    },
    {
      title: "Climate Smart Agriculture Trends 2026",
      description:
        "New techniques are helping reduce environmental impact."
    }
  ];

  const { hasFeatureByKey, uplGenieEnabledFeatures } = useRbac();

  const { username, fullName: fullname, ugdnId: userUGDN } = useAuth();

  const [message, setMessage] = useState("");
  const [suggestionAPICall, setSuggestionAPICall] = useState(false);
  const [cardClicked, setCardClicked] = useState(false);
  const chatEndRef = useRef(null);
  const [converstation, setConverstation] = useState([]);
  const converstationRef = useRef([]);
  const [uiConversation, setUIConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState({});
  const [feedbackInput, setFeedbackInput] = useState({});
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [showWebConsent, setShowWebConsent] = useState(false);
  // eslint-disable-next-line 
  const [selectedRoleDetails, setSelectedRoleDetails] = useState([]);
  const isMobile = useIsMobile();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [editedText, setEditedText] = useState("");
  const richInputRef = useRef(null);
  const streamReaderRef = useRef(null);
  const streamAbortControllerRef = useRef(null);
  const fileUploadCancelTokenRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [docUploadChunks, setDocUploadChunks] = useState([]);
  const [productCitations, setProductCitations] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sourceLinks, setSourcesLinks] = useState([]);
  const [isTextBoxDisabled, setIsTextBoxDisabled] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [models, setModels] = useState([]);
  const [temporaryModel, setTemporaryModel] = useState(null);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const pendingSendMessageOptionsRef = useRef(null);

  // getters
  const modelResponse = useSelector((state) => state?.api?.getModelList);
  const selectedModal = useSelector((state) => state.state.selectedModal);
  const selectedAgent = useSelector((state) => state?.state?.selectedAgent);
  const webSearchEnabled = useSelector((state) => state?.state?.webSearchEnabled ?? false);
  const isFileUploaded = useSelector((state) => state?.state?.isFileUploaded ?? false);
  const thinkingEnabled = useSelector((state) => state?.state?.thinkingEnabled ?? false);
  const selectedTool = useSelector((state) => state?.state?.selectedTool);

  const chatToolItems = useMemo(() => {
    const items = [
      { id: KEYS.SESSION.IMAGE_GENERATION, name: constants.IMAGE_GENERATION, image: imageCreationIcon, activeImage: activeImageCreationIcon }
    ];

    if (hasFeatureByKey(RBAC_APPS.CHAT_GENIE, UplGenieFeatures.DOC_UPLOAD) && false) {
      items.push({ id: KEYS.SESSION.EXCEL_ANALYSIS, name: constants.EXCEL_ANALYSIS, image: excelAnalysisIcon, activeImage: activeExcelAnalysisIcon });
    }

    return items;
  }, [constants, hasFeatureByKey]);

  const prevWebSearchRef = useRef(webSearchEnabled);
  const prevSelectedModelRef = useRef(null);
  const { prompts: suggestedPrompts, refetch: refetchSuggested } = usePrompts(KEYS.PROMPT.SUGGESTED);
  const { refetch: refetchPersonal } = usePrompts(KEYS.PROMPT.PERSONAL, { enabled: false });

  const [activeSuggestiveCategory, setActiveSuggestiveCategory] = useState(null);

  useEffect(() => {
    if (suggestedPrompts) {
      setCategories(formatPrompts(suggestedPrompts));
    }
  }, [suggestedPrompts]);

  useEffect(() => {
    converstationRef.current = converstation;
  }, [converstation]);

  useEffect(() => {
    const unsubscribe = subscribe("NewChatDirect", (preserveQuery = false) => {
      handleNewSessionContinue(preserveQuery);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const rolesMap = {
      [HR_ASSISTANT]: {
        id: roles.length + 1,
        name: HR_ASSISTANT,
        text: `Hi, I’m your smart HR assistant!
  I help you quickly find answers to global and India-specific HR policies, from leaves and benefits to workplace guidelines.
  Available 24/7, I make navigating HR simple, fast, and stress-free.`,
      },
    };

    const unsubscribe = subscribe("myApps", async (isSelected, data) => {
      await emit("resetSelectedThread", true);
      clearChatData();

      if (isSelected) {
        const selectedRole = rolesMap[data];
        if (selectedRole) {
          handleRoleClick(selectedRole);
        }
      } else {
        setCurrentRole(null);
      }
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, dispatch]);


  useEffect(() => {
    const rawData = modelResponse?.data;
    if (!rawData) return;

    // Switch between standard models ('data') and image tool models
    const sourceModels = selectedTool === KEYS.SESSION.IMAGE_GENERATION ? (rawData.image_tool_models || []) : (rawData.data || []);
    if (!sourceModels.length) return;

    // Filter standard models by features, keeping image tool models as is
    const enabledFeatures = uplGenieEnabledFeatures || {};
    const filteredData = selectedTool === KEYS.SESSION.IMAGE_GENERATION ? sourceModels : filterModelsByFeatures(sourceModels, enabledFeatures);

    const activeModel = temporaryModel || selectedModal;
    const enriched = Array.isArray(filteredData)
      ? filteredData.map((item) => ({
        ...item,
        isSelected: item?.name === activeModel,
      }))
      : [];

    setModels(enriched);

    // If current selectedModal is NOT in the new list, switch to a valid fallback
    const isModelInList = enriched.some(m => m.name === activeModel);
    if (!isModelInList && enriched.length > 0) {
      const fallback = enriched[0];
      dispatch(setSelectedGPTModal(fallback?.name));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelResponse, selectedModal, temporaryModel, uplGenieEnabledFeatures, dispatch, selectedTool]);

  const handleCategoryClick = (label) => {
    setActiveSuggestiveCategory(prev =>
      prev === label ? null : label
    );
    setMessage("");
    if (suggestedPrompts) {
      setCategories(formatPrompts(suggestedPrompts));
    }
  };

  const handleOutputModalGenerationSelect = (index, option) => {
    const selectedModelName = option?.name || "Auto-Select";

    if (index !== undefined && index !== null) {
      // Per-message regeneration: use temporaryModel so global selection is unaffected
      setTemporaryModel(selectedModelName);
      const prevIndex = index - 1;
      if (prevIndex >= 0) {
        const editData = uiConversation[prevIndex];
        handleGenerationSave(prevIndex, editData, selectedModelName);
      }
    } else {
      // Global model change from dropdown: persist to Redux and clear any temp override
      setTemporaryModel(null);
      dispatch(setSelectedGPTModal(selectedModelName));
    }
  };

  const handleGenerationSave = async (editIndex, editData, explicitModel = null) => {
    const editingIndex = uiConversation.findIndex(
      (msg, index) => index === editIndex && msg.role === "user"
    );
    if (editingIndex === -1) {
      return;
    }

    // Get the previous user message for regeneration
    const previousUserMessage = uiConversation[editIndex];

    await handleSendMessage({
      messageID: previousUserMessage.id,
      selectedModel: explicitModel || temporaryModel || selectedModal,
      query: editData.content,
      regenerationFilePaths: previousUserMessage.file_paths || [],
    });
  };

  const handleRoleClick = async (role) => {
    setCurrentRole(role);

    if (activeTab !== role.id) {
      addMessagesFunction(role?.name, role.text);
    }

    setActiveTab(activeTab === role?.id ? null : role?.id);

    await stopStreaming();
    await clearChatData();
    textToSpeech.stop();
  };


  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setSelectedPdfFile(null);
  };


  useEffect(() => {
    const unsubscribe = subscribe("clearChat", () => {
      clearChatData();
      setActiveTab(null);
      setCurrentRole(null);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    const sessionId = sessionManager.getSessionId("sessionId");
    const userId = username;
    if (sessionId && userId) {
      fetchUserConversation(userId, sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearChatData = async (preserveQuery = false) => {
    setConverstation([]);
    setUIConversation([]);
    setSelectedFeedback({});
    localStorage.removeItem("logout");
    setIsChatLoading(false);
    sessionManager.setSessionId(null, "sessionId");
    await emit("resetSelectedThread", true);
    setActiveSuggestiveCategory(null);

    // Clear message and RichInputBox when starting new chat
    if (!preserveQuery) {
      setMessage("");
      if (richInputRef.current) {
        richInputRef.current.clear();
      }
    }
    dispatch(setSelectedTool(null));

    dispatch(setWebSearchEnabled(false));
    dispatch(setThinkingEnabled(false));
  };

  const addMessagesFunction = (_, text) => {
    setTimeout(() => {
      const botMessage = { sender: "bot", text: text, message_id: null };
      setSelectedRoleDetails(botMessage);
    }, 50);
  };

  const ensureSessionId = () => {
    let currentSessionId = sessionManager.getSessionId("sessionId");
    if (!currentSessionId || currentSessionId === "undefined" || currentSessionId === null) {
      try {
        currentSessionId = generateSessionId();
        setSessionId(currentSessionId);
        sessionManager.setSessionId(currentSessionId, "sessionId");
      } catch (error) {
        console.error("Error generating session ID:", error);
      }
    }
    return currentSessionId;
  };

  const handlePromptEnhancing = async () => {
    const currentMessage = message?.trim();
    if (!currentMessage) return;

    const currentSessionId = ensureSessionId();

    setIsEnhancing(true);

    try {
      const payload = {
        query: currentMessage,
        session_id: currentSessionId || "",
      };

      let response;
      if (selectedTool) {
        response = await dispatch(fetchImageToolEnhancePrompt(payload));
      } else {
        response = await dispatch(fetchEnhanceQueryRequest(payload));
      }

      const enhancedPrompt = response?.enhanced_query;

      if (enhancedPrompt && enhancedPrompt !== currentMessage) {
        handleInputChange(enhancedPrompt);
      }
    } catch (error) {
      console.error("Error enhancing prompt:", error);
      toast.error(constants.chat.FAILED_TO_ENHANCE_PROMPT || "Failed to enhance prompt");
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleFileUpload = async (files, userMessageId, sessionID) => {
    const metadata = {
      user_id: username,
      ugdn_id: userUGDN || '',
      session_id: sessionID,
      message_id: userMessageId,
      files: files,
    };

    // Create cancel token source for this upload
    const cancelTokenSource = axios.CancelToken.source();
    fileUploadCancelTokenRef.current = cancelTokenSource;

    try {
      const response = selectedTool === KEYS.SESSION.EXCEL_ANALYSIS
        ? await dispatch(handleChatExcelFileUpload(metadata, cancelTokenSource))
        : await dispatch(uploadFileData(metadata, cancelTokenSource));

      if (response?.status === "success") {
        return { ok: true, data: response };
      }

      if (response?.status === "error") {
        toast.error(response.message || constants.chat.FILE_UPLOAD_FAILED || "File upload failed");
        setIsLoading(false);
        return;
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return { ok: false, error: "Upload cancelled" };
      }
      console.error("Upload exception:", error);
      return { ok: false, error: error.message };
    } finally {
      fileUploadCancelTokenRef.current = null;
    }
  };

  const handleStopStreaming = () => {
    if (streamAbortControllerRef.current) {
      try {
        streamAbortControllerRef.current.abort();
      } catch (err) {
        console.warn("AbortController error:", err);
      }
    }

    // Cancel any ongoing file upload
    if (fileUploadCancelTokenRef.current) {
      fileUploadCancelTokenRef.current.cancel("Upload cancelled by user");
      fileUploadCancelTokenRef.current = null;
    }

    setIsLoading(false);

    const updateLastPlaceholder = prevConvo =>
      prevConvo.map((msg, idx, arr) =>
        idx === arr.length - 1 && msg.isPlaceholder
          ? {
            ...msg,
            isPlaceholder: false,
            content:
              msg.content === "Loading..." || msg.content.trim() === ""
                ? "Streaming Stopped"
                : msg.content,
          }
          : msg
      );

    setUIConversation(updateLastPlaceholder);
    setConverstation(updateLastPlaceholder);

    if (updateLastPlaceholder.length < 3) {
      setTimeout(() => {
        emit("updateThreads");
      }, 2000);
    }
  };

  const handleSendMessage = async ({
    isEdit = false,
    editedConvo = [],
    editedMessage = null,
    messageID = null,
    selectedModel = null,
    query = null,
    regenerationFilePaths = null,
    consentValue = null,
    baseConversation = null,
  } = {}) => {
    if (isLoading && !isEdit) {
      return;
    }
    emit("querySent");

    const editFilePath =
      converstation.find(msg => msg.role === "user" && msg.id === messageID)?.file_paths || [];

    const newFilePaths =
      selectedFiles?.map(file => ({
        path: getDocxUrlFromBase64(file.content),
        filename: file.name || "file",
      })) || [];

    // For regeneration, use the provided file paths directly
    const regenerationPaths = regenerationFilePaths || [];

    const filePaths = isEdit
      ? (newFilePaths.length > 0 ? newFilePaths : editFilePath)
      : regenerationPaths.length > 0 ? regenerationPaths : newFilePaths;

    const finalFiles = isEdit ? (newFilePaths.length > 0 ? selectedFiles : []) : regenerationPaths.length > 0 ? [] : selectedFiles;

    const userMessageId = generateCustomId();
    const contentToSend = message?.trim()
      ? message
      : query ? query : filePaths.length > 0
        ? sanitizeTextInput(filePaths.map(f => f.filename).join(" "))
        : "";

    const currentTimestamp = new Date().toISOString();
    const userMessage = isEdit
      ? {
        role: "user",
        content: editedMessage?.content || contentToSend,
        id: messageID,
        timestamp: currentTimestamp,
        ...(filePaths.length > 0 ? { file_paths: filePaths } : {}),
      }
      : {
        role: "user",
        content: contentToSend,
        id: userMessageId,
        timestamp: currentTimestamp,
        ...(filePaths.length > 0 ? { file_paths: filePaths } : {}),
      };

    const placeholderBotMessage = {
      role: "assistant",
      content: "Loading...",
      isFileUploading: finalFiles.length > 0,
      isPlaceholder: true,
      id: isEdit ? messageID : userMessageId,
      timestamp: currentTimestamp,
      feedback: "",
      session_type: selectedTool,
    };

    const baseConvo = baseConversation ?? converstationRef.current;

    const updatedUI = isEdit
      ? [...editedConvo, userMessage, placeholderBotMessage]
      : [...baseConvo, userMessage, placeholderBotMessage];
    setUIConversation(updatedUI);
    setConverstation(updatedUI);

    scrollToLatestViewPoint(chatEndRef, isMobile, false);
    setSuggestions([]);

    if (!isEdit) {
      setMessage("");
      // Clear the RichInputBox
      if (richInputRef.current) {
        richInputRef.current.clear();
      }
    }

    if (!query) setTemporaryModel(null);
    setIsLoading(true);

    // Reset file upload cancellation token for new message
    fileUploadCancelTokenRef.current = null;

    const sessionId = ensureSessionId();

    const messagesForAPI = isEdit
      ? [...editedConvo, userMessage]
      : [...baseConvo, userMessage];

    setSelectedFiles([]);

    if (finalFiles.length > 0) {
      try {
        const fileUploadResponse = await handleFileUpload(finalFiles, isEdit ? messageID : userMessageId, sessionId);
        if (!fileUploadResponse?.ok) {
          console.error("File upload failed");
          handleAbortMessageError(messagesForAPI);
          return;
        }
      } catch (uploadError) {
        console.error("File upload error:", uploadError);
        handleAbortMessageError(messagesForAPI);
        return;
      }
    }

    const role = currentRole?.name || (selectedTool === KEYS.SESSION.PRODUCT_LABELS ? KEYS.ROLE_TYPES.PRODUCT_LABEL :
      selectedTool === KEYS.SESSION.EXCEL_ANALYSIS ? KEYS.ROLE_TYPES.EXCEL_ANALYSIS : "");
    const modelToUse = selectedModel || selectedModal;

    const payload = {
      ugdn_id: userUGDN || "",
      user_id: username,
      chat_id: `UID${sessionId.slice(0, 3)}`,
      message_id: isEdit ? messageID : userMessageId,
      session_id: sessionId,
      tool_ids: [],
      is_doc_upload: finalFiles.length > 0 || isFileUploaded ? "True" : "False",
      features: {},
      variables: {},
      model: modelToUse,
      messages: messagesForAPI,
      isEdit: isEdit ? "True" : "False",
      editMessageId: isEdit ? messageID : "",
      stream: true,
      query: isEdit ? userMessage.content : contentToSend,
      role_name: role,
      role_flag: role ? "True" : "False",
      params: {},
      files: filePaths.length > 0 ? filePaths.map(f => f.filename) : null,
      web_search: filePaths.length > 0 ? false : webSearchEnabled,
      session_type: selectedTool === KEYS.SESSION.IMAGE_GENERATION ? selectedTool : "CHAT",
      ...(modelToUse && !modelToUse.toLowerCase().includes("grok") ? { thinking_enabled: thinkingEnabled } : {}),
      ...(consentValue ? { web_search_consent: consentValue } : {}),
      user_info: {
        country: "India"
      }
    };

    if (!hasFeatureByKey(RBAC_APPS.CHAT_GENIE, UplGenieFeatures.WEB_SEARCH)) {
      delete payload.web_search;
    }

    pendingSendMessageOptionsRef.current = {
      isEdit,
      editedConvo,
      editedMessage,
      messageID,
      selectedModel,
      query: query || contentToSend,
      regenerationFilePaths,
      baseConversation: [...converstation],
    };

    await handleSendMessageAPI(payload, userMessageId, placeholderBotMessage, messageID, isEdit, messagesForAPI);
  };

  const handleAbortMessageError = (messagesForAPI) => {
    const errorMessage = {
      role: "assistant",
      content: "Sorry, I'm having trouble right now",
      feedback: '',
      timestamp: new Date().toISOString(),
    };
    const updatedConvo = [...messagesForAPI, errorMessage];
    setConverstation(updatedConvo);
    setUIConversation(updatedConvo);
    streamReaderRef.current = null;
    setIsLoading(false);
  };

  const normalizeChunksByMessageId = ({ message_id, doc_chunks_info }) =>
    Object.entries(doc_chunks_info || {}).reduce((acc, [filename, items]) => {
      acc[message_id] = [
        ...(acc[message_id] || []),
        ...items.map(item => ({ ...item, filename, message_id })),
      ];
      return acc;
    }, {});

  const formatRetrievedDocs = (retrievedDocs, messageId) => {
    if (!retrievedDocs) return null;
    if (Array.isArray(retrievedDocs)) return retrievedDocs;
    if (typeof retrievedDocs === 'object') {
      return normalizeChunksByMessageId({ message_id: messageId, doc_chunks_info: retrievedDocs })[messageId];
    }
    return null;
  };

  const handleSendMessageAPI = async (payload, userMessageId, placeholderBotMessage, messageID, isEdit, messagesForAPI) => {
    // Helper to push the latest streamed content to the UI
    const updateStreamUI = (text, imgData) =>
      setUIConversation([...messagesForAPI, { ...placeholderBotMessage, content: text, image_data: imgData }]);

    // Create a controller so we can abort the stream if the user stops it
    const abortController = new AbortController();
    streamAbortControllerRef.current = abortController;

    try {
      const response =
        selectedTool === KEYS.SESSION.PRODUCT_LABELS
          ? await fetchProductLabelAIChatCompletions(payload, abortController)
          : selectedTool === KEYS.SESSION.EXCEL_ANALYSIS ? await fetchChatExcelCompletions(payload, abortController)
            : await fetchChatCompletions(payload, abortController);

      if (abortController.signal.aborted) return;

      // Session expired — clear storage and send user to login
      if (response?.status === 401) {
        handleUnauthorized(navigate, undefined, undefined, constants.SESSION_EXPIRED);
        return;
      }

      if (!response.ok || !response.body) throw new Error("Streaming failed");

      const reader = response.body.getReader();
      streamReaderRef.current = reader;
      const decoder = new TextDecoder("utf-8");

      let streamedText = "";
      let metadata = null;
      let accumulatedEncryptedData = "";
      let isMetaEnd = false;
      let imageData = null;
      let leftover = "";
      let finalSuggestivePrompts = null;

      // Read the stream chunk by chunk until the server signals done
      while (true) {
        if (abortController.signal.aborted) return;
        const { value, done } = await reader.read();
        if (abortController.signal.aborted) return;
        let decodedChunk = decoder.decode(value || new Uint8Array(), { stream: !done });
        let combined = leftover + decodedChunk;
        let lines = combined.split("\n");
        // The last element is either an empty string (if chunk ended with \n) 
        // or a partial line (if it didn't). Keep it for the next iteration.
        leftover = lines.pop() || "";

        if (done && leftover) {
          lines.push(leftover);
          leftover = "";
        }

        for (const line of lines) {
          if (!line) continue;
          if (line.startsWith("{")) {
            try {
              const event = JSON.parse(line);
              if (event?.type === "web_search_consent_required") {
                setIsLoading(false);
                setShowWebConsent(true);
                return;
              }
            } catch (e) {
              console.error(e);
            }
          }

          if (line.startsWith("data:")) {
            // Accumulate encrypted metadata sent by the server
            accumulatedEncryptedData += line.slice(5).trim();

          } else if (line.startsWith("meta_end:")) {
            // Server signals that all metadata chunks have been sent
            if (line.slice(9).trim() === "true") isMetaEnd = true;

          } else if (line === "image" || line === "image_start") {
            // Initialize image object when stream starts
            imageData = { isLoading: true, base64_image: "" };
            updateStreamUI(streamedText, imageData);

          } else if (line.startsWith("image_data:")) {
            // Accumulate base64 chunks for the image
            if (imageData) {
              let chunk = line.slice(11).trim();
              // Strip prefix if present in the first chunk
              if (!imageData.base64_image && chunk.includes(",")) {
                chunk = chunk.slice(chunk.indexOf(",") + 1);
              }
              imageData.base64_image = (imageData.base64_image || "") + chunk;
              updateStreamUI(streamedText, imageData);
            }

          } else if (line === "image_end") {
            // Finalize the image when stream ends
            if (imageData) {
              imageData.isLoading = false;
              updateStreamUI(streamedText, imageData);
            }
          } else {
            try {
              // Decrypt and append each streamed text token to the chat bubble
              streamedText += encrypted.decrypt(line);
              updateStreamUI(streamedText, imageData);
            } catch {
              // Ignore lines that fail decryption (incomplete or invalid chunks)
            }
          }
        }

        if (done) break;

        // Once all metadata is received, decrypt and process the full metadata object
        if (isMetaEnd && accumulatedEncryptedData) {
          try {
            const parsed = JSON.parse(encrypted.decrypt(accumulatedEncryptedData));
            metadata = parsed;

            // Replace the loading image placeholder with the real generated image
            if (parsed?.image_data) {
              imageData = formatImageData(imageData, parsed);
              updateStreamUI(streamedText, imageData);
            }

            // Refresh the thread list if the title or thread placement changed
            if (parsed?.title_flag || parsed?.thread_move_flag === true) emit("updateThreads");

            // Sync document upload state and disable web search if a doc was uploaded
            const fileUploaded = !!parsed?.is_doc_upload;
            dispatch(setFileUploaded(fileUploaded));
            if (fileUploaded) dispatch(setWebSearchEnabled(false));

            // Save AI-suggested follow-up prompts to be displayed after response is complete
            if (parsed?.suggestive_prompts?.length > 0) {
              finalSuggestivePrompts = parsed.suggestive_prompts;
            }

            // Fetch citation chunks for the uploaded document if present
            if ((parsed?.has_documents && parsed?.is_doc_upload) || (parsed?.role_name === KEYS.ROLE_TYPES.EXCEL_ANALYSIS && parsed?.retrieved_docs && parsed?.is_doc_upload)) {
              try {
                const citationPayload = {
                  message_id: parsed?.message_id,
                  session_id: parsed?.session_id,
                  retrieved_docs: parsed?.retrieved_docs || [],
                };
                const citationResponse = await dispatch(fetchDocumentCitationChunks(citationPayload));
                if (citationResponse) {
                  setDocUploadChunks((prev) => ({ ...prev, ...normalizeChunksByMessageId(citationResponse) }));
                }
              } catch (error) {
                console.error("Error fetching document citation chunks:", error);
              }
            }

            // Check RAG retrieval and format citations
            const isProductLabelRole = parsed?.role === KEYS.ROLE_TYPES.PRODUCT_LABEL;
            if (parsed?.is_rag_retrieval || isProductLabelRole) {
              const docsToFormat = parsed?.retrieved_docs || parsed?.doc_chunks_info;
              if (docsToFormat) {
                const formattedDocs = formatRetrievedDocs(docsToFormat, parsed.message_id);
                if (formattedDocs) {
                  setProductCitations((prev) => ({ ...prev, [parsed.message_id]: formattedDocs }));
                }
              }
            }

            // Ensure message_id is always stored on the metadata object
            if (parsed.message_id && !metadata?.message_id) metadata = { ...metadata, message_id: parsed.message_id };

            // Store web search citations keyed by message ID for the Sources panel
            if (parsed?.web_citations?.length > 0 && parsed?.message_id) {
              setSourcesLinks((prev) => ({ ...prev, [parsed.message_id]: parsed.web_citations }));
            }

            // Append any additional text that arrived as part of the metadata payload
            if (parsed.response_text) {
              streamedText += parsed.response_text;
              setUIConversation([...messagesForAPI, {
                ...placeholderBotMessage,
                content: streamedText,
                id: isEdit ? messageID : userMessageId,
                image_data: imageData,
                is_thinking: !!(parsed?.is_thinking || metadata?.is_thinking),
                is_web_search: !!(parsed?.is_web_search || metadata?.is_web_search),
              }]);
            }

            // Reset accumulators for the next metadata block
            accumulatedEncryptedData = "";
            isMetaEnd = false;
          } catch (err) {
            console.warn("Failed to decrypt metadata:", err);
          }
        }
      }

      if (abortController.signal.aborted) return;

      // Commit the final complete message to conversation state
      const finalBotMessage = {
        role: "assistant",
        content: streamedText || metadata?.response_text || "",
        id: isEdit ? messageID : userMessageId,
        image_data: formatImageData(imageData, metadata),
        model_deployment: metadata?.model_deployment || payload?.model || "",
        is_thinking: !!metadata?.is_thinking,
        is_web_search: !!metadata?.is_web_search,
        feedback: "",
      };
      const finalConvo = [...messagesForAPI, finalBotMessage];
      setConverstation(finalConvo);
      setUIConversation(finalConvo);
      if (finalSuggestivePrompts?.length > 0) {
        setSuggestions(finalSuggestivePrompts);
      }

    } catch (error) {
      // User manually stopped the stream — no error to show
      if (error.name === "AbortError" || abortController.signal.aborted) return;
      console.error("Chat error:", error);
      setSuggestions([]);
      handleAbortMessageError(messagesForAPI);
    } finally {
      // Always clean up stream refs and re-enable the input box
      streamReaderRef.current = null;
      streamAbortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  const handleSavePrompt = async (data) => {
    try {
      const isUpdate = !!data.id;
      const payload = {
        type: KEYS.PROMPT.PERSONAL,
        prompt_name: data.title,
        prompt_text: data.prompt,
        department: data.department === "All" ? "" : data.department,
        task: data.task === "All" ? "" : data.task,
        is_active: true,
      };

      if (isUpdate) {
        payload.id = data.id;
        payload.display_order = data.display_order ?? 0;
      }

      const result = await dispatch(managePrompts(payload));
      if (result && !result.error) {
        toast.info(data.id ? (constants.chat.PROMPT_UPDATE_SUCCESS || "Prompt updated successfully") : (constants.chat.PROMPT_SAVE_SUCCESS || "Prompt saved successfully"));
        refetchPersonal(true);
        refetchSuggested(true);
      } else {
        toast.error(constants.chat.PROMPT_SAVE_FAILED || "Failed to save prompt");
      }
    } catch (error) {
      console.error("Error saving prompt:", error);
      toast.error(constants.chat.PROMPT_SAVE_FAILED || "Failed to save prompt");
    }
  };

  const handleDeletePrompt = async (prompt) => {
    try {
      const payload = {
        type: KEYS.PROMPT.PERSONAL,
        id: prompt.id,
        is_active: false,
      };
      const result = await dispatch(managePrompts(payload));
      if (result && !result.error) {
        toast.info(constants.chat.PROMPT_DELETE_SUCCESS || "Prompt deleted successfully");
        refetchPersonal(true);
        refetchSuggested(true);
      } else {
        toast.error(constants.chat.PROMPT_DELETE_FAILED || "Failed to delete prompt");
      }
    } catch (error) {
      console.error("Error deleting prompt:", error);
      toast.error(constants.chat.PROMPT_DELETE_FAILED || "Failed to delete prompt");
    }
  };

  const handleWebConsent = async (consent) => {
    try {
      const { baseConversation, ...options } = pendingSendMessageOptionsRef.current || {};
      pendingSendMessageOptionsRef.current = null;

      const session_id = sessionManager.getSessionId("sessionId") || sessionId;

      await dispatch(updateWebSearchConsent({ session_id, consent }));

      const isAccepted = consent === KEYS.CONSENT.ACCEPTED;
      if (isAccepted) {
        dispatch(setWebSearchEnabled(true));
      }

      const geminiModel = models.find((m) => m.name?.toLowerCase().includes("gemini"));
      const modelToUse = isAccepted ? (geminiModel?.name || options.selectedModel) : options.selectedModel || selectedModal;

      setShowWebConsent(false);

      await handleSendMessage({
        ...options,
        selectedModel: modelToUse,
        consentValue: consent,
        baseConversation
      });

    } catch (err) {
      console.error("Consent API failed:", err);
    }
  };

  const stopStreaming = () => {
    if (streamAbortControllerRef.current) {
      try {
        streamAbortControllerRef.current.abort();
      } catch (err) {
        console.warn("AbortController error:", err);
      }
      streamAbortControllerRef.current = null;
    }

    if (streamReaderRef.current) {
      try {
        const cancelPromise = streamReaderRef.current.cancel();
        if (cancelPromise && cancelPromise.catch) {
          cancelPromise.catch(() => { });
        }
      } catch (err) {
        console.warn("Stream reader cancel error:", err);
      }
      streamReaderRef.current = null;
    }

    // Cancel any ongoing file upload
    if (fileUploadCancelTokenRef.current) {
      fileUploadCancelTokenRef.current.cancel("Upload cancelled by user");
      fileUploadCancelTokenRef.current = null;
    }

    setIsLoading(false);
    setConverstation([]);
    setUIConversation([]);
  };

  const fetchUserConversation = async (user_id, session_id) => {
    setIsChatLoading(true);
    handleCancelAudioPlay();
    setConverstation([]);
    setUIConversation([]);
    setActiveTab(null);
    setCurrentRole(null);
    setSuggestions([]);
    setDocUploadChunks([]);
    setProductCitations([]);
    setSourcesLinks([]);
    if (!user_id || !session_id) {
      handleNewSessionContinue();
      return;
    }
    try {
      const params = {
        session_id: session_id,
      };
      const response = await dispatch(fetchUserConversationRequest(params));
      let conversionData = response;

      const match = roles.find((role) => role.name === conversionData?.role_name);
      if (match) {
        setCurrentRole(match);
        addMessagesFunction(match.name, match.text);
        setActiveTab(match.id);
        stopStreaming();
      }
      setSuggestions(conversionData?.suggestive_prompts || []);
      setDocUploadChunks(conversionData?.doc_chunks_info || []);

      const citationsMap = conversionData?.retrieved_docs ? JSON.parse(JSON.stringify(conversionData.retrieved_docs)) : {};
      const isProductLabel = conversionData?.role_name === KEYS.ROLE_TYPES.PRODUCT_LABEL;
      if (conversionData?.data && Array.isArray(conversionData.data) && isProductLabel) {
        conversionData.data.forEach((msg) => {
          const docsToFormat = msg.doc_chunks_info || msg.retrieved_docs;
          if (docsToFormat) {
            const formattedDocs = formatRetrievedDocs(docsToFormat, msg.id);
            if (formattedDocs) citationsMap[msg.id] = formattedDocs;
          }
        });
      }
      setProductCitations(citationsMap);

      if (conversionData?.is_doc_upload) {
        dispatch(setFileUploaded(true));
        dispatch(setWebSearchEnabled(false));
      } else if (conversionData?.is_web_search === true) {
        dispatch(setFileUploaded(false));
        dispatch(setWebSearchEnabled(true));
      }
      else {
        dispatch(setFileUploaded(false));
        dispatch(setWebSearchEnabled(false));
      }

      // Restore thinking state from conversation
      if (conversionData?.is_thinking === true) {
        dispatch(setThinkingEnabled(true));
      } else {
        dispatch(setThinkingEnabled(false));
      }

      const role = conversionData?.role_name || "";
      if (role === KEYS.ROLE_TYPES.PRODUCT_LABEL) {
        dispatch(setSelectedTool(KEYS.SESSION.PRODUCT_LABELS));
      } else if (role === KEYS.ROLE_TYPES.EXCEL_ANALYSIS) {
        dispatch(setSelectedTool(KEYS.SESSION.EXCEL_ANALYSIS));
      } else if (conversionData?.session_type === KEYS.SESSION.IMAGE_GENERATION) {
        dispatch(setSelectedTool(KEYS.SESSION.IMAGE_GENERATION));
      } else {
        dispatch(setSelectedTool(null));
      }

      setUIConversation(conversionData?.data || []);
      setConverstation(conversionData?.data || []);

      // Map sources from each message's web_citations
      const sourcesMap = {};
      if (conversionData?.data && Array.isArray(conversionData.data)) {
        conversionData.data.forEach((message) => {
          if (message?.web_citations && message.id) {
            try {
              const citations = typeof message.web_citations === 'string'
                ? JSON.parse(message.web_citations)
                : message.web_citations;

              if (Array.isArray(citations) && citations.length > 0) {
                sourcesMap[message.id] = citations;
              }
            } catch (error) {
              console.warn(`Failed to parse web_citations for message ${message.id}:`, error);
            }
          }
        });
      }
      setSourcesLinks(sourcesMap);

      // Map temporaryModel with last uiConversation data model_deployment
      if (conversionData?.data && conversionData.data.length > 0) {
        const lastConversationItem = conversionData.data[conversionData.data.length - 1];
        if (lastConversationItem?.model_deployment) {
          const modelName = getModelInfo(lastConversationItem.model_deployment, models);
          setTemporaryModel(modelName);
        }
      }

      setIsChatLoading(false);
      scrollToLatestViewPoint(chatEndRef, isMobile, true);
      // return data;
    } catch (error) {
      setIsChatLoading(false);
      console.error("Error fetching user conversation:", error);
      throw error;
    }
  };

  function handleSearchSuggestion(text) {
    if (isLoading) return;
    setMessage(text);
    setSuggestionAPICall(true);
  }

  useEffect(() => {
    if (suggestionAPICall === true) {
      handleSendMessage();
      setSuggestionAPICall(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionAPICall]);

  useEffect(() => {
    // Load selectedFeedback
    const savedSelectedFeedback = JSON.parse(
      localStorage.getItem("selectedFeedback")
    );
    if (savedSelectedFeedback) {
      setSelectedFeedback(savedSelectedFeedback);
    }
  }, []);

  const handleInputChange = (text) => {
    const inputValue = text;
    const filteredValue = inputValue.split('').join('');
    setMessage(filteredValue);

    const searchText = filteredValue.toLowerCase().trim();

    if (searchText) {
      setActiveSuggestiveCategory(null);
    }

    if (!searchText) {
      setFilteredPrompts([]);
      return;
    }

    // Filter categories
    const filtered = categories
      .map(category => ({
        ...category,
        prompts: category.prompts.filter(prompt =>
          prompt.toLowerCase().includes(searchText)
        )
      }))
      .filter(category => category.prompts.length > 0);

    setFilteredPrompts(filtered);
  };

  const handleMediaClick = async (file) => {
    if (!file) return;

    try {
      let objectUrl = "";
      let filename = file.filename;

      const fileType = getFileTypeCategory(file);

      // Case 1: PDF — open preview immediately
      if (fileType === "pdf") {
        setSelectedPdfFile({ ...file, path: "", isLoading: true });
        setIsPreviewOpen(true);
      }

      if (file.path && file.path.startsWith("blob:")) {
        objectUrl = file.path;
      }
      else if (file?.decrypted_base64) {
        const byteCharacters = file.decrypted_base64;
        objectUrl = getDocxUrlFromBase64(byteCharacters);
      }
      else {
        // Download File data to view
        let response;
        if (selectedTool === KEYS.SESSION.PRODUCT_LABELS) {
          response = await dispatch(fetchProductLabelFileBytesData({ chunk_id: file.chunk_id, file_name: file.file_name || filename }));
          objectUrl = URL.createObjectURL(response);
          filename = file.file_name || filename;
        } else {
          response = await dispatch(fetchFileBytesData({ file_id: file.file_id }));
          const { file_name, file_data } = response;
          filename = file_name || filename;
          objectUrl = getDocxUrlFromBase64(file_data);
        }
      }

      // PDF preview
      if (fileType === "pdf") {
        setSelectedPdfFile({ ...file, path: objectUrl, isLoading: false });
        return;
      }

      await downloadFile({ url: objectUrl, filename });

    } catch (err) {
      console.error("Error fetching file:", err);
      toast.error(constants.chat.FAILED_TO_LOAD_FILE || "Failed to load file");
      setSelectedPdfFile((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleEditClick = (index, role, id) => {
    if (role === "user") {
      setEditingId(index);
      setEditedText(uiConversation[index].content);
    }
  };

  const handleSave = async (editIndex, editData) => {
    // Keep only entries with keys < editIndex
    const updatedFeedback = {};
    for (const key in selectedFeedback) {
      if (parseInt(key) < editIndex) {
        updatedFeedback[key] = selectedFeedback[key];
      }
    }
    setSelectedFeedback(updatedFeedback);
    setEditingId(null);
    if (!editedText.trim()) return;
    const editingIndex = converstation.findIndex(
      (msg, index) => index === editIndex && msg.role === "user"
    );
    if (editingIndex === -1) {
      return;
    }
    stopStreaming();

    // Clear sources and citations for the message ID being edited
    if (editData?.id) {
      setSourcesLinks((prev) => {
        const updated = { ...prev };
        delete updated[editData.id];
        return updated;
      });
    }

    const updatedUserMessage = {
      role: "user",
      content: sanitizeTextPreservingHTML(editedText),
    };
    const trimmedConvo = converstation.slice(0, editingIndex);
    await handleSendMessage({
      isEdit: true,
      editedConvo: trimmedConvo,
      editedMessage: updatedUserMessage,
      messageID: editData?.id,
    });

    setEditedText("");
  };


  useEffect(() => {
    const unsubscribe = subscribe("thread", async (agentType, sessionId) => {
      if (agentType !== "CHAT_GENIE") return;

      if (!sessionId) return;

      const localSessionId = sessionManager.getSessionId('sessionId');

      if (localSessionId === sessionId) {
        setSessionId(sessionId);
        return;
      }

      setSessionId(sessionId);
      setSelectedFiles([]);
      dispatch(setFileUploaded(false));
      setEditingId(null);
      fetchUserConversation(username, sessionId);
      stopStreaming();
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe]);

  useEffect(() => {
    if (cardClicked) {
      handleSendMessage();
      setCardClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardClicked]);

  const [showScrollButton, setShowScrollButton] = useState(false);
  const chatContainerRef = useRef(null);

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isAtBottom);
    }
  };

  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  useEffect(() => {
    handleScroll();
  }, [uiConversation]);


  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise().catch((err) =>
        console.error("MathJax typeset error:", err)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [converstation]);

  const prevSelectedTool = useRef(selectedTool);
  useEffect(() => {
    if (prevSelectedTool.current !== selectedTool) {
      if (selectedFiles.length > 0) {
        setSelectedFiles([]);
        dispatch(setFileUploaded(false));
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
      prevSelectedTool.current = selectedTool;
    }
  }, [selectedTool, selectedFiles.length, dispatch]);

  const handleNewSessionContinue = async (preserveQuery = false) => {
    if (location?.pathname !== '/chat') {
      navigate('/chat');
    }
    stopStreaming();
    setActiveTab(null);
    setCurrentRole(null);
    await emit("resetSelectedThread", true);
    clearChatData(preserveQuery);
    setSelectedFiles([]);
    handleCancelAudioPlay();
    dispatch(setFileUploaded(false));
    setTemporaryModel(null);
    setIsLoading(false);
    setIsChatLoading(false);
    sessionManager.setSessionId(null, "sessionId");
    dispatch(setWebSearchEnabled(false));
    dispatch(setThinkingEnabled(false));
  };

  const handleFeedback = async (index, type, data, comment = "") => {
    // Update UI conversation with feedback type
    setUIConversation((prev) =>
      prev.map((msg, idx) =>
        idx === index ? { ...msg, feedback: type } : msg
      )
    );

    setConverstation((prev) =>
      prev.map((msg, idx) =>
        idx === index ? { ...msg, feedback: type } : msg
      )
    );

    // Update feedback selection
    setSelectedFeedback((prev) => ({
      ...prev,
      [index]: type,
    }));

    if (comment) {
      setFeedbackInput((prev) => ({
        ...prev,
        [index]: { comment },
      }));
    }

    await handleFeedbackSubmit(type, index, data, comment);
  };

  const handleFeedbackSubmit = async (type, index, pair, commentParam = "") => {
    if (submittingFeedback) return;

    setSubmittingFeedback(true);

    try {
      const newSessionId = sessionManager.getSessionId("sessionId") || sessionId;

      const comment = sanitizeTextInput(
        commentParam !== "" ? commentParam : (feedbackInput?.[index]?.comment?.trim() || "")
      );

      const requestBody = {
        session_id: newSessionId,
        message_id: pair?.id ?? "",
        feedback: type ?? "",
        feedback_comment: comment,
        user_id: username,
      };

      const feedbackAction = updateChatFeedbackRequest;

      await dispatch(feedbackAction(requestBody)).unwrap?.();

      toast.info(constants.chat.FEEDBACK_SUBMIT_SUCCESS || "Feedback submitted successfully");

      setFeedbackInput((prev) => ({
        ...prev,
        [index]: { comment: "" },
      }));
    } catch (error) {
      console.error("Feedback submit error:", error);
      toast.error(constants.chat.FEEDBACK_SUBMIT_FAILED || "Failed to submit feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const MAX_FILE_COUNT = UPL_GENIE_DOCUMENT_FILE_COUNT_LIMIT;
  const MAX_FILE_SIZE = UPL_GENIE_DOCUMENT_FILE_SIZE_LIMIT;

  const handleFileChange = async (e) => {
    const newFiles = Array.from(e.target.files);
    if (!newFiles.length) return;

    const maxAllowedFiles = selectedTool === KEYS.SESSION.EXCEL_ANALYSIS ? 1 : MAX_FILE_COUNT;

    const limitValidation = validateFileLimits({
      newFiles,
      selectedFilesLength: selectedFiles.length,
      maxFileSize: MAX_FILE_SIZE,
      maxAllowedFiles,
      constants
    });

    if (!limitValidation.valid) {
      toast.error(limitValidation.errorMsg);
      e.target.value = "";
      return;
    }

    let allowedFileExtensions = [
      "docx", "pdf", "txt", "pptx", "html", "ppt",
      "csv", "xlsx", "json", "xml", "png", "jpg", "jpeg"
    ];

    if (selectedTool === KEYS.SESSION.EXCEL_ANALYSIS) {
      allowedFileExtensions = ["xlsx", "csv"];
    }

    const { validFiles, invalidFiles } = await validateFiles(newFiles, allowedFileExtensions);
    if (invalidFiles.length > 0) {
      displayInvalidFileErrors(invalidFiles, toast, constants);
      e.target.value = "";
      return;
    }

    try {
      const filesData = await Promise.all(
        validFiles.map(async (file) => ({
          name: file.name,
          type: file.type,
          size: file.size,
          content: await fileToBase64(file),
        }))
      );

      setSelectedFiles((prev) => [...prev, ...filesData]);

      dispatch(setFileUploaded(true));
      dispatch(setWebSearchEnabled(false));

    } catch (err) {
      console.error("Error reading files:", err);
      toast.error(constants.chat.FAILED_TO_READ_FILES || "Failed to read one or more files.");
    }

    e.target.value = "";
  };

  useEffect(() => {
    return () => {
      // Stop any ongoing speech when component unmounts
      textToSpeech.stop();
    };
  }, []);

  const handleCancelAudioPlay = () => {
    textToSpeech.stop();
  };

  useEffect(() => {
    if (selectedAgent !== "Chat Genie") return;
    if (!models || models.length === 0) return;

    const prevWebSearch = prevWebSearchRef.current;

    // Web Search turned ON → save current model, switch to Gemini
    if (!prevWebSearch && webSearchEnabled) {
      // Remember what model the user had before web search was enabled
      prevSelectedModelRef.current = selectedModal;
      // Use Gemini if available, otherwise fall back to the first model
      const geminiModel = models.find(
        (model) => model.name?.toLowerCase().includes("gemini")
      );
      const modelToSelect = geminiModel || models[0];
      if (modelToSelect) {
        dispatch(setSelectedGPTModal(modelToSelect.name));
      }
    }

    // Web Search turned OFF → restore the model the user had before
    if (prevWebSearch && !webSearchEnabled) {
      const modelToRestore = prevSelectedModelRef.current || models[0]?.name;
      if (modelToRestore) {
        dispatch(setSelectedGPTModal(modelToRestore));
      }
      prevSelectedModelRef.current = null;
    }

    prevWebSearchRef.current = webSearchEnabled;
  }, [webSearchEnabled, models, selectedAgent, selectedModal, CHAT_GENIE, dispatch]);

  useEffect(() => {
    // Only disable the textbox when loading, not when empty
    setIsTextBoxDisabled(isLoading);
  }, [isLoading]);

  const hasChatMessages = (uiConversation?.length ?? 0) > 0;

  return (
    <>
      <div className={`upl-genie-page-wrapper ${!hasChatMessages && !isMobile ? 'is-empty' : 'is-active'}`}>
        {!hasChatMessages && false && (
          <NewsBanner news={staticNews} />
        )}

        {/* Main container — fills remaining space */}
        <div className="upl-genie-container">
          <LoadingScreen isLoading={isChatLoading} />

          {/* Chat body */}
          <div className="upl-chat-body" ref={chatContainerRef}>
            <div className="upl-chat-content-column">
              {!hasChatMessages && !isChatLoading ? (
                <div className="upl-new-chat-content">
                  <Dashboard
                    icon={botIcon}
                    title={`${constants.WELCOME}, ${fullname}!`}
                    categories={categories}
                    categoryCards={[
                      {
                        heading: constants.IMAGE_GENERATION,
                        icon: activeImageCreationIcon,
                        items: [constants.IMAGE_GENERATION_CARD_SUBTITLE],
                        theme: "blue",
                        highlight: selectedTool === KEYS.SESSION.IMAGE_GENERATION,
                        onCardClick: () => dispatch(setSelectedTool(
                          selectedTool === KEYS.SESSION.IMAGE_GENERATION
                            ? null
                            : KEYS.SESSION.IMAGE_GENERATION
                        )),
                      },
                      {
                        heading: constants.PROMPT_GALLERY,
                        icon: promptLibraryIcon,
                        items: [constants.PROMPT_GALLERY_SUBTITLE],
                        theme: "blue",
                        onCardClick: () => emit("openPromptLibrary"),
                      },
                      {
                        heading: categories[0]?.label || constants.CATEGORIES_CARD_HEADING,
                        icon: dailyTask,
                        items: [constants.CATEGORIES_CARD_SUBTITLE],
                        theme: "blue",
                        onCardClick: () => categories[0] && handleCategoryClick(categories[0].label),
                      }
                    ]}
                    activeCategory={activeSuggestiveCategory}
                    filteredPrompts={filteredPrompts}
                    inputValue={message}
                    onPromptClick={(prompt) => {
                      handleInputChange(prompt);
                      setActiveSuggestiveCategory(null);
                      setFilteredPrompts([]);
                    }}
                  />
                </div>
              ) : (
                <div className="upl-messages-list">
                  {!isChatLoading && (uiConversation || []).map((pair, index) => {
                    const isLastMessage = index === uiConversation.length - 1;
                    const currentBotIcon = botIcon;
                    const messageModelName = pair?.model_deployment
                      ? getModelInfo(pair.model_deployment, models)
                      : null;
                    const previousUserMsg = uiConversation.slice(0, index).reverse().find(m => m?.role === 'user');
                    const hasFiles = previousUserMsg?.file_paths?.length > 0;

                    return (
                      <MessageBox
                        key={index}
                        pair={pair}
                        index={index}
                        editingId={editingId}
                        editedText={editedText}
                        setEditedText={setEditedText}
                        isMobile={isMobile}
                        fullname={fullname}
                        isTextBoxDisabled={isTextBoxDisabled}
                        showEditPrompt={true}
                        handleEditClick={handleEditClick}
                        handleSave={handleSave}
                        setEditingId={setEditingId}
                        botIcon={currentBotIcon}
                        isLoading={isLoading}
                        isLastMessage={isLastMessage}
                        showTimeStamp={pair.role === "user"}
                        formatMessageTime={formatMessageTime}
                        renderMediaSection={(p) => <MediaSection pair={p} handleMediaClick={handleMediaClick} />}
                        useMathJax={true}
                        onRegenerate={handleOutputModalGenerationSelect}
                        onFeedback={handleFeedback}
                        models={models}
                        feedbackState={pair.feedback}
                        suggestions={isLastMessage && !isLoading ? suggestions : []}
                        citations={[...(docUploadChunks?.[pair?.id] || []), ...(productCitations?.[pair?.id] || [])]}
                        sourceLinks={sourceLinks?.[pair?.id] || []}
                        charLimit={UPL_GENIE_PROMPT_CHAR_LIMIT}
                        onSuggestionClick={handleSearchSuggestion}
                        showFeedback={(
                          !hasFiles && !webSearchEnabled && selectedTool !== KEYS.SESSION.EXCEL_ANALYSIS
                            ? ["like", "dislike", "copy", "audio", "regenerate", "pdf", "docx"]
                            : ["like", "dislike", "copy", "audio", "pdf", "docx"]
                        )}
                        generatedBy={messageModelName}
                        onCitationClick={handleMediaClick}
                        isWebSearch={!!pair.is_web_search}
                        thinkingEnabled={thinkingEnabled}
                        onSavePrompt={true}
                      />
                    );
                  })}
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Unified Footer */}
          {!isChatLoading && (
            <div className="upl-genie-footer">
              <div className="upl-chat-content-column">
                {/* Scroll-to-bottom button */}
                {hasChatMessages && showScrollButton && (
                  <div className="upl-scroll-btn-row">
                    <button
                      className="upl-scroll-bottom-btn"
                      onClick={() => scrollToLatestViewPoint(chatEndRef, isMobile, true, true)}
                    >
                      <img src={downArrow} alt="Scroll to bottom" />
                    </button>
                  </div>
                )}

                <ChatPromptContainer
                  value={message}
                  onChange={handleInputChange}
                  isLoading={isLoading}
                  isEnhancing={isEnhancing}
                  charLimit={UPL_GENIE_PROMPT_CHAR_LIMIT}
                  placeholder={GENIE_START_PROMPT || 'Ask me anything...'}
                  handleSendMessage={handleSendMessage}
                  handleStopStreaming={handleStopStreaming}
                  models={selectedTool === KEYS.SESSION.EXCEL_ANALYSIS ? [] : models}
                  selectedModel={selectedModal}
                  onModelChange={(opt) => handleOutputModalGenerationSelect(null, { name: opt })}
                  isModelDisabled={isLoading || webSearchEnabled}
                  showEnhance={true}
                  showImageGeneration={true}
                  showDocUpload={hasFeatureByKey(RBAC_APPS.CHAT_GENIE, UplGenieFeatures.DOC_UPLOAD) && (selectedTool !== KEYS.SESSION.PRODUCT_LABELS && selectedTool !== KEYS.SESSION.IMAGE_GENERATION)}
                  showWebSearch={hasFeatureByKey(RBAC_APPS.CHAT_GENIE, UplGenieFeatures.WEB_SEARCH) && (selectedTool !== KEYS.SESSION.EXCEL_ANALYSIS && selectedTool !== KEYS.SESSION.IMAGE_GENERATION)}
                  showSavePrompt={!selectedTool}
                  onSavePrompt={handleSavePrompt}
                  onDeletePrompt={handleDeletePrompt}
                  handleFileChange={handleFileChange}
                  handlePromptEnhancing={handlePromptEnhancing}
                  showThinking={!selectedTool}
                  selectedFiles={selectedFiles}
                  setSelectedFiles={setSelectedFiles}
                  fileInputRef={fileInputRef}
                  richInputRef={richInputRef}
                  categories={
                    (!hasChatMessages && isMobile)
                      ? []
                      : !hasChatMessages
                        ? (message ? filteredPrompts : categories)
                        : []
                  }
                  activeCategory={(!hasChatMessages && isMobile) ? null : (!hasChatMessages ? activeSuggestiveCategory : null)}
                  onSuggestivePromptClick={(prompt) => {
                    handleInputChange(prompt);
                    setActiveSuggestiveCategory(null);
                    setFilteredPrompts([]);
                  }}
                  hasMessages={hasChatMessages}
                  onNewSession={handleNewSessionContinue}
                  toolItems={chatToolItems}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {isPreviewOpen && (
        <PdfPreview
          selectedFile={selectedPdfFile}
          isOpen={isPreviewOpen}
          onClose={handleClosePreview}
          isMobile={isMobile}
        />
      )}
      {showWebConsent && (
        <Modal open={showWebConsent}>
          <div className="modal-inner-content">
            <div className="modal-title">{constants.WEB_SEARCH_LABEL}</div>
            <div className="modal-message">
              {WEB_SEARCH_CONFIRM_MESSAGE}
            </div>
            <div className="modal-buttons">
              <button
                className="button-style primary-grey-button"
                onClick={() => {
                  handleWebConsent(KEYS.CONSENT.DISMISSED);
                }}
              >
                {constants.CANCEL}
              </button>
              <button
                className="button-style primary-button"
                onClick={() => {
                  handleWebConsent(KEYS.CONSENT.ACCEPTED);
                }}
              >
                {constants.ENABLE_WEB_SEARCH}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default Chat;